import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowLeft, Loader2, CheckCircle2, XCircle } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function AdminEmployee() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/staff/${id}`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [id]);

  if (data === null)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  if (data === false) return <p className="py-20 text-center text-muted-foreground">Employee not found.</p>;

  const { user, history } = data;
  const passed = history.filter((h) => h.status === "Passed").length;
  const pct = history.length ? Math.round((passed / history.length) * 100) : 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <button
        data-testid="back-to-admin"
        onClick={() => navigate("/admin")}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </button>

      <div className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 shadow-[0_8px_30px_rgba(37,75,54,0.06)]">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary font-heading text-xl font-extrabold text-primary-foreground">
          {user.name.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">{user.name}</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
        <div className="text-right">
          <p className="font-heading text-2xl font-extrabold text-primary" data-testid="employee-completion">
            {pct}%
          </p>
          <p className="text-xs text-muted-foreground">complete</p>
        </div>
      </div>

      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Training history</p>
        <Accordion type="multiple" className="space-y-3">
          {history.map((h) => (
            <AccordionItem
              key={h.module_id}
              value={h.module_id}
              data-testid={`history-module-${h.module_id}`}
              className="overflow-hidden rounded-2xl border border-border bg-card shadow-[0_8px_30px_rgba(37,75,54,0.06)]"
            >
              <AccordionTrigger className="px-5 py-4 hover:no-underline">
                <div className="flex w-full flex-wrap items-center justify-between gap-2 pr-2 text-left">
                  <span className="font-heading font-bold tracking-tight">{h.module_title}</span>
                  <StatusBadge status={h.status} />
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <div className="grid grid-cols-2 gap-3 rounded-xl bg-secondary/50 p-4 text-sm sm:grid-cols-4">
                  <Field label="Started" value={fmt(h.started_at)} />
                  <Field label="Completed" value={fmt(h.completed_at)} />
                  <Field label="Latest score" value={h.latest_score != null ? `${h.latest_score}%` : "—"} />
                  <Field label="Attempts" value={h.attempts} />
                </div>

                {h.attempt_history.length === 0 ? (
                  <p className="mt-4 text-sm text-muted-foreground">No quiz attempts yet.</p>
                ) : (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                      All attempts
                    </p>
                    {h.attempt_history.map((a, i) => (
                      <div
                        key={i}
                        data-testid={`attempt-${h.module_id}-${i}`}
                        className="flex items-center justify-between rounded-xl border border-border bg-background px-4 py-2.5"
                      >
                        <span className="flex items-center gap-2 text-sm">
                          {a.passed ? (
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                          ) : (
                            <XCircle className="h-4 w-4 text-destructive" />
                          )}
                          {fmt(a.created_at)}
                        </span>
                        <span className={`font-heading font-bold ${a.passed ? "text-primary" : "text-destructive"}`}>
                          {a.score}% <span className="text-xs font-normal text-muted-foreground">({a.correct}/{a.total})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}
